import type { ReactNode } from 'react';
import {
  ChevronRight,
  ClipboardList,
  Clock,
  DoorOpen,
  FileText,
  Lock,
  MapPin,
  Users,
  Video,
  type LucideIcon,
} from 'lucide-react';
import { Button, Progress, Separator } from '@clarix/ui';
import { Badge } from '@clarix/ui-shared';
import { cn } from '@clarix/ui/lib/cn';
import { Faces, PulseDot } from './chipParts';
import type { ChipView } from './chipView';
import { CHIP, QM_POLL } from './constants';
import { CHIP_COPY } from './copy';
import { LiveMeetingControls } from './LiveMeetingControls';
import {
  conferenceAppName,
  eventLabel,
  formatClock,
  formatDayClock,
  formatDayRange,
  formatElapsed,
  formatSpan,
  isPrivateMeeting,
  meetingPeople,
} from './format';
import type { ChipPerson, NextMeetingAction, PendingAction, WireEvent } from './types';
import { useNow } from './useNow';

const MIN = 60_000;

function IconTile({ icon: Icon, className }: { icon: LucideIcon; className?: string }) {
  return (
    <span
      className={cn(
        'bg-muted text-muted-foreground flex h-7 w-7 shrink-0 items-center justify-center rounded-lg',
        className,
      )}
      aria-hidden
    >
      <Icon className="h-3.5 w-3.5" />
    </span>
  );
}

interface HeroProps {
  /** The tint, in the colour of the chip's badge. */
  className: string;
  badge: ReactNode;
  meta?: string | null;
  metaClassName?: string;
  title: ReactNode;
  children?: ReactNode;
}

function Hero({ className, badge, meta, metaClassName, title, children }: HeroProps) {
  return (
    <div className={cn('rounded-t-xl px-4 pb-4 pt-3.5', className)}>
      <div className="flex items-center justify-between gap-2">
        {badge}
        {meta && <span className={cn('text-sm font-medium tabular-nums', metaClassName)}>{meta}</span>}
      </div>
      <h2
        title={typeof title === 'string' ? title : undefined}
        className="text-foreground mt-2.5 line-clamp-2 break-words text-3xl font-semibold leading-tight"
      >
        {title}
      </h2>
      {children}
    </div>
  );
}

type Phase = 'live' | 'soon' | 'next';

const PHASE_LOOK: Record<Phase, { tint: string; meta: string; track: string; fill: string }> = {
  live: { tint: 'bg-success-light/70', meta: 'text-success-fg', track: 'bg-success/15', fill: 'bg-success' },
  soon: { tint: 'bg-info-light/70', meta: 'text-info-fg', track: 'bg-info/15', fill: 'bg-info' },
  next: { tint: 'bg-muted/60', meta: 'text-primary', track: '', fill: '' },
};

const percent = (part: number, whole: number) => (whole > 0 ? Math.min(100, Math.max(0, (part / whole) * 100)) : 100);

interface HeroState {
  badge: ReactNode;
  meta: string | null;
  progress: number | null;
  progressLabel: string;
}

function heroState(view: ChipView, focus: WireEvent, at: number): HeroState {
  const start = Date.parse(focus.start);
  if (view.kind === 'live') {
    const end = Date.parse(focus.end);
    const minsLeft = Math.ceil((end - at) / MIN);
    return {
      badge: (
        <Badge tone="green" variant="solid" size="sm" className="tabular-nums">
          <PulseDot />
          {CHIP_COPY.liveFor(formatElapsed(at - start))}
        </Badge>
      ),
      meta: minsLeft > 0 ? CHIP_COPY.left(formatSpan(minsLeft)) : CHIP_COPY.endingNow,
      progress: percent(at - start, end - start),
      progressLabel: CHIP_COPY.meetingProgress,
    };
  }
  if (view.kind === 'next') {
    return {
      badge: (
        <Badge tone="gray" size="sm" className="bg-background">
          {CHIP_COPY.upNext}
        </Badge>
      ),
      meta: view.relative,
      progress: null,
      progressLabel: '',
    };
  }
  const msLeft = start - at;
  const soft = view.kind === 'soon';
  return {
    badge:
      msLeft > 0 ? (
        <Badge
          tone="blue"
          variant={soft ? 'soft' : 'solid'}
          size="sm"
          className={cn('tabular-nums', soft && 'bg-background')}
        >
          {CHIP_COPY.startsIn(formatElapsed(Math.ceil(msLeft / 1000) * 1000))}
        </Badge>
      ) : (
        <Badge tone="blue" variant="solid" size="sm">
          <PulseDot />
          {CHIP_COPY.startingNow}
        </Badge>
      ),
    meta: view.joinOpens ? CHIP_COPY.joinOpens(view.joinOpens) : null,
    progress: percent(CHIP.SOON_MS - msLeft, CHIP.SOON_MS),
    progressLabel: CHIP_COPY.startProgress,
  };
}

function firstName(name: string): string {
  return name.trim().split(/\s+/)[0] ?? name;
}

function peopleNames(people: ChipPerson[]): string {
  const first = people.slice(0, CHIP.FACES_MAX).map((p) => firstName(p.name));
  return CHIP_COPY.names(first, people.length - first.length);
}

/** Names one kind only when every waiting meeting is listed and they all agree. */
function needsSummary(count: number, actions: NextMeetingAction[]): string {
  const kinds = new Set<PendingAction>(actions.map((a) => a.action));
  const [only] = kinds;
  return kinds.size === 1 && only && count === actions.length
    ? CHIP_COPY.needsSummary[only]
    : CHIP_COPY.needsSummary.both;
}

interface DetailRowProps {
  icon: LucideIcon;
  /** Read before the row's text, where the icon alone carries what it is. */
  srLabel?: string;
  children: ReactNode;
}

function DetailRow({ icon, srLabel, children }: DetailRowProps) {
  return (
    <li className="flex min-w-0 items-center gap-2.5 text-lg">
      <IconTile icon={icon} />
      {srLabel && <span className="sr-only">{srLabel}: </span>}
      {children}
    </li>
  );
}

interface MainCardProps {
  view: ChipView;
  focus: WireEvent;
  now: number;
  h24: boolean;
  later: WireEvent[];
  onOpenEvent: (e: WireEvent) => void;
  onOpenTodo: () => void;
  /** The live meeting's Extend menu or End dialog opened or closed. */
  onLayerChange: (open: boolean) => void;
}

/** The card behind the chip: the focus meeting up top, then where / how / who, then the rest of the day. */
export function MainCard({ view, focus, now, h24, later, onOpenEvent, onOpenTodo, onLayerChange }: MainCardProps) {
  const counting = view.kind === 'soon' || view.kind === 'imminent' || view.kind === 'starting';
  // The chip ticks every 30 s; the countdown here reads in seconds, so it keeps its own clock.
  const tick = useNow(QM_POLL.PILL_TICK_MS, counting);
  const at = counting ? Math.max(now, tick) : now;
  const phase: Phase = view.kind === 'live' ? 'live' : counting ? 'soon' : 'next';
  const look = PHASE_LOOK[phase];
  const hero = heroState(view, focus, at);
  const { place, attendees, joinUrl } = view;
  const app = conferenceAppName(focus.conference.app);

  return (
    <>
      <Hero
        className={look.tint}
        badge={hero.badge}
        meta={hero.meta}
        metaClassName={look.meta}
        title={
          isPrivateMeeting(focus) ? (
            <span className="flex items-center gap-2">
              <Lock className="text-muted-foreground h-4 w-4 shrink-0" aria-hidden />
              {eventLabel(focus)}
            </span>
          ) : (
            eventLabel(focus)
          )
        }
      >
        <p className="text-md text-muted-foreground mt-1 tabular-nums">{formatDayRange(focus, h24)}</p>
        {hero.progress !== null && (
          <Progress
            value={hero.progress}
            aria-label={hero.progressLabel}
            className={cn('mt-3 h-1.5', look.track)}
            indicatorClassName={look.fill}
          />
        )}
      </Hero>
      {(place || app || attendees.length > 0) && (
        <ul className="flex flex-col gap-2.5 px-4 pt-3.5">
          {place && (
            <DetailRow icon={place.kind === 'room' ? DoorOpen : MapPin} srLabel={CHIP_COPY[place.kind]}>
              <span title={place.name} className="text-foreground min-w-0 truncate font-medium">
                {place.name}
              </span>
              {place.detail && (
                <span title={place.detail} className="text-muted-foreground max-w-[45%] shrink-0 truncate">
                  {place.detail}
                </span>
              )}
            </DetailRow>
          )}
          {app && (
            <DetailRow icon={Video}>
              <span className="text-foreground font-medium">{app}</span>
            </DetailRow>
          )}
          {attendees.length > 0 && (
            <DetailRow icon={Users}>
              <Faces people={attendees} showMore={false} />
              <span title={peopleNames(attendees)} className="text-muted-foreground min-w-0 truncate">
                {peopleNames(attendees)}
              </span>
            </DetailRow>
          )}
        </ul>
      )}
      <div className="flex flex-col gap-2 px-4 pb-4 pt-3.5">
        <div className="flex gap-2">
          {joinUrl && (
            <Button asChild size="sm" className="flex-1">
              <a href={joinUrl} target="_blank" rel="noopener noreferrer">
                <Video aria-hidden />
                {CHIP_COPY.joinMeeting}
              </a>
            </Button>
          )}
          <Button
            type="button"
            variant="outline"
            size="sm"
            className={cn('px-3', !joinUrl && 'flex-1')}
            aria-label={CHIP_COPY.openInCalendar}
            onClick={() => onOpenEvent(focus)}
          >
            {joinUrl ? CHIP_COPY.open : CHIP_COPY.openInCalendar}
          </Button>
        </div>
        {view.kind === 'live' && <LiveMeetingControls event={focus} h24={h24} onLayerChange={onLayerChange} />}
      </div>
      {later.length > 0 && (
        <>
          <Separator />
          <section aria-label={CHIP_COPY.laterToday} className="p-2">
            <h3 className="text-muted-foreground px-2 pb-1 pt-0.5 text-sm font-medium uppercase tracking-wide">
              {CHIP_COPY.laterToday}
            </h3>
            <ul>
              {later.map((e) => (
                <li key={e.id}>
                  <button
                    type="button"
                    onClick={() => onOpenEvent(e)}
                    className="hover:bg-accent focus-visible:bg-accent flex w-full min-w-0 items-center gap-2.5 rounded-md px-2 py-1.5 text-left focus-visible:outline-none"
                  >
                    <span className="text-md text-muted-foreground w-14 shrink-0 tabular-nums">
                      {formatClock(e.start, h24)}
                    </span>
                    <span title={eventLabel(e)} className="text-foreground min-w-0 flex-1 truncate text-lg font-medium">
                      {eventLabel(e)}
                    </span>
                    <Faces people={meetingPeople(e)} size="sm" />
                  </button>
                </li>
              ))}
            </ul>
          </section>
        </>
      )}
      {view.count > 0 && (
        <>
          <Separator />
          <button
            type="button"
            onClick={onOpenTodo}
            className="hover:bg-muted/50 focus-visible:bg-muted/50 flex w-full items-center gap-2.5 rounded-b-xl px-4 py-3 text-left transition-colors focus-visible:outline-none"
          >
            <IconTile icon={ClipboardList} className="bg-violet-light text-violet-fg" />
            <span className="flex min-w-0 flex-1 flex-col">
              <span className="text-foreground text-lg font-medium">{CHIP_COPY.needYou(view.count)}</span>
              <span className="text-muted-foreground text-sm">{needsSummary(view.count, view.actions)}</span>
            </span>
            <ChevronRight className="text-muted-foreground h-3.5 w-3.5 shrink-0" aria-hidden />
          </button>
        </>
      )}
    </>
  );
}

/** Behind the all-clear chip: nothing booked, so the way into Calendar. */
export function AllClearCard({ onOpenCalendar }: { onOpenCalendar: () => void }) {
  return (
    <>
      <Hero
        className="bg-success-light/70"
        badge={
          <Badge tone="green" size="sm" className="bg-background">
            {CHIP_COPY.allClear}
          </Badge>
        }
        title={CHIP_COPY.free}
      />
      <div className="px-4 pb-4 pt-3.5">
        <Button type="button" variant="outline" size="sm" className="w-full" onClick={onOpenCalendar}>
          {CHIP_COPY.openCalendar}
        </Button>
      </div>
    </>
  );
}

const ACTION_LOOK: Record<PendingAction, { icon: LucideIcon; tile: string }> = {
  mom: { icon: FileText, tile: 'bg-violet-light text-violet-fg' },
  allocate: { icon: Clock, tile: 'bg-warning-light text-warning-fg' },
};

interface TodoCardProps {
  /** The server's whole count; `actions` is only the oldest few. */
  count: number;
  actions: NextMeetingAction[];
  h24: boolean;
  onOpenAction: (a: NextMeetingAction) => void;
  onOpenCalendar: () => void;
}

export function TodoCard({ count, actions, h24, onOpenAction, onOpenCalendar }: TodoCardProps) {
  const more = Math.max(0, count - actions.length);
  return (
    <>
      <Hero
        className="bg-violet-light/70"
        badge={
          <Badge tone="violet" variant="solid" size="sm" className="tabular-nums">
            {CHIP_COPY.waitingCount(count)}
          </Badge>
        }
        title={CHIP_COPY.waitingOnYou}
      >
        <p className="text-md text-muted-foreground mt-1">{CHIP_COPY.waitingBody}</p>
      </Hero>
      <ul className="py-1.5">
        {actions.map((a) => {
          const look = ACTION_LOOK[a.action];
          const label = eventLabel(a.event);
          return (
            <li key={`${a.action}-${a.event.id}`} className="flex w-full items-center gap-3 px-4 py-2.5">
              <IconTile icon={look.icon} className={look.tile} />
              <span className="flex min-w-0 flex-1 flex-col">
                <span title={label} className="text-foreground truncate text-lg font-medium">
                  {label}
                </span>
                <span className="text-muted-foreground text-sm">
                  {`${CHIP_COPY.actionDue[a.action]} · ${formatDayClock(a.event.start, h24)}`}
                </span>
              </span>
              <Button
                type="button"
                variant="outline"
                size="xs"
                className="shrink-0"
                aria-label={CHIP_COPY.actionFor[a.action](label)}
                onClick={() => onOpenAction(a)}
              >
                {CHIP_COPY.actionVerb[a.action]}
              </Button>
            </li>
          );
        })}
      </ul>
      {more > 0 && <p className="text-md text-muted-foreground px-4 pb-2">{CHIP_COPY.moreWaiting(more)}</p>}
      <Separator />
      <div className="p-1">
        <Button type="button" variant="ghost" size="sm" className="text-primary w-full" onClick={onOpenCalendar}>
          {CHIP_COPY.seeAll(count)}
        </Button>
      </div>
    </>
  );
}
